import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { PlusIcon, XIcon } from "@heroicons/react/outline";
import { adaptPlaygroundBlocks, type BlockOverrides, ContentBlocks } from "@promptlayer/ui";
import {
  BracesIcon,
  DatabaseIcon,
  FileBoxIcon,
  GripVertical,
  MoreVerticalIcon,
  TypeOutlineIcon,
} from "lucide-react";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useCallback, useMemo } from "react";

import { Citations } from "@/components/Citations";
import CodeAccordion from "@/components/CodeAccordion";
import ContentArea from "@/components/ContentArea";
import { withExternalChanges } from "@/components/ContentArea/external-update-context";
import LoadingSpinner from "@/components/LoadingSpinner";
import McpAccordion from "@/components/McpAccordion";
import { MESSAGE_DETAILS } from "@/components/Playground/Messages/constants";
import CollapsibleMessageContent from "@/components/Playground/Messages/Message/CollapsibleMessageContent";
import FullscreenDialog from "@/components/Playground/Messages/Message/FullscreenDialog";
import useFullscreen from "@/components/Playground/Messages/Message/hooks/useFullscreen";
import useInputVariableDecorations from "@/components/Playground/Messages/Message/hooks/useInputVariableDecorations";
import { IMessageStore } from "@/components/Playground/Messages/Message/message-store";
import MessageActions from "@/components/Playground/Messages/Message/MessageActions";
import MessageParsingError from "@/components/Playground/Messages/Message/MessageParsingError";
import {
  MessageSubmitButton,
  MessageSubmitI,
} from "@/components/Playground/Messages/Message/MessageSubmitButton";
import PlaceholderMessagePreview from "@/components/Playground/Messages/Message/PlaceholderMessagePreview";
import ScopedInputVariablePopover from "@/components/Playground/Messages/Message/ScopedInputVariablePopover";
import RoleSelector from "@/components/Playground/Messages/Message/RoleSelector";
import ToolCalls from "@/components/Playground/Messages/Message/ToolCalls";
import ToolCallStatusShimmer from "@/components/Playground/Messages/Message/ToolCallStatusShimmer";
import { usePlayground } from "@/components/Playground/playground-store";
import { PLAYGROUND_SESSION_MODES, PlaygroundSessionModeType } from "@/components/Playground/types";
import ThinkingAccordion from "@/components/ThinkingAccordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MediaBlock, OutputMediaBlock } from "@/components/ui/MediaBlocks";
import PrettyJsonView from "@/components/ui/PrettyJsonView";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { useAuth } from "@/context/auth-context";
import { cn } from "@/lib/utils";
import { useUpload } from "@/queries";
import { CacheControl, Content, Media, OutputMediaProviderMetadata, TextContent } from "@/types";
import { getPrettifiableJsonText } from "@/utils/jsonDisplay";
import { detectMediaTypeFromUrl, getMediaTypeFromMimeType } from "@/utils/media";
import { formatMcpError } from "@/utils/utils";

const PlaceholderMessageInput = ({
  message,
  readonly,
  leftBraces,
  rightBraces,
}: {
  message: IMessageStore;
  readonly: boolean;
  leftBraces: string;
  rightBraces: string;
}) => (
  <div className="flex flex-col">
    <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 focus-within:border-gray-200">
      <span className="text-sm text-gray-500">{leftBraces}</span>
      <Input
        readOnly={readonly}
        placeholder={message.placeholder || "variable_name"}
        onChange={e => message.setMessage(e.target.value)}
        value={message.content}
        className="!min-h-0 flex-1 !border-0 bg-transparent px-1 font-mono text-sm !outline-none !ring-0 focus:border-none focus:outline-none focus:ring-0"
      />
      <span className="text-sm text-gray-500">{rightBraces}</span>
    </div>

    {message.content && <PlaceholderMessagePreview variableName={message.content} />}
  </div>
);

const MessageContentDisplay = ({
  isLiveToolCall,
  isToolCallInProgress,
  hasThinkingContent,
  hasCodeContent,
  hasOutputMediaContent,
  readonly,
  toolCallStatus,
  messageRole,
  message,
  leftBraces,
  rightBraces,
  children,
}: {
  isLiveToolCall: boolean;
  isToolCallInProgress: boolean;
  hasThinkingContent: boolean;
  hasCodeContent: boolean;
  hasOutputMediaContent: boolean;
  readonly: boolean;
  toolCallStatus: string;
  messageRole: string;
  message: IMessageStore;
  leftBraces: string;
  rightBraces: string;
  children: React.ReactNode;
}) => {
  const playground = usePlayground();

  if (
    (isToolCallInProgress || (isLiveToolCall && !message.hasReceivedText)) &&
    !hasThinkingContent &&
    !hasCodeContent &&
    !hasOutputMediaContent &&
    readonly &&
    playground.operationStore.isRunning
  ) {
    return (
      <ToolCallStatusShimmer
        status={toolCallStatus || "Generating..."}
        className="min-h-[70px] w-full"
        infoText={
          playground.isImageGenerationMode ? "Image generation can take 1-2 minutes." : undefined
        }
      />
    );
  }

  if (messageRole === "placeholder") {
    return (
      <PlaceholderMessageInput
        message={message}
        readonly={readonly}
        leftBraces={leftBraces}
        rightBraces={rightBraces}
      />
    );
  }

  return <>{children}</>;
};

interface MessageProps extends MessageSubmitI {
  className?: string;
  contentBlocksClassName?: string;

  message: IMessageStore;
  enableJsonOutputDetection?: boolean;
  isCompletion?: boolean;
  isFirst?: boolean;
  isOutputMessageComplete?: boolean;
  readonly?: boolean;
  extraHeader?: React.ReactNode | null;
  index?: number;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  /** Rendering context - "batch" for batch mode, "chat" for chat session mode */
  mode?: PlaygroundSessionModeType;
  isDragging?: boolean;

  //TODO convert block actions into single config object
  isBlockActionsPositionFixed?: boolean;
  isBlockActionsAlwaysVisible?: boolean;
  isBlockActionsSizeXS?: boolean;

  /** Extends in MessageSubmitI */
  /** Callback for sending message in chat mode */
  // onSend?: () => void;
  /** Loading state when sending message */
  // isSending?: boolean;
}

interface MessageSkeletonProps {
  /** When true, shows the same shimmer as message generating state (ToolCallStatusShimmer) for pending outputs or loading run details */
  showGeneratingState?: boolean;
  infoText?: string;
}

export const MessageSkeleton = ({
  showGeneratingState = false,
  infoText,
}: MessageSkeletonProps) => (
  <div className="group flex h-full w-full flex-col gap-y-1 rounded-lg px-3 py-3">
    <div className="flex items-center justify-between gap-x-2 pb-2">
      <div className="flex w-full items-center gap-x-2">
        <div className="w-[180px]">
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
      </div>
      <div className="flex gap-x-2">
        <Skeleton className="h-8 w-8 rounded-md" />
      </div>
    </div>
    <div className="relative z-[1] flex h-full w-full flex-col gap-y-1">
      {showGeneratingState ? (
        <ToolCallStatusShimmer
          status="Generating..."
          className="min-h-[200px] w-full"
          infoText={infoText}
        />
      ) : (
        <Skeleton className="min-h-[200px] w-full rounded" />
      )}
    </div>
  </div>
);

export const AddNewMessageBlock = () => {
  const playground = usePlayground();
  const onClick = playground.addEmptyMessage;
  return (
    <div
      className="group my-3 flex w-full cursor-pointer flex-col gap-y-1 rounded-lg border border-dashed border-gray-200 px-3 py-3 transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm"
      onClick={onClick}
      role="button"
      aria-label="Add new message"
    >
      <div className="flex items-center justify-between gap-x-2 pb-2">
        <div className="flex w-full items-center gap-x-2">
          <div className="flex h-9 w-[180px] items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-sm text-gray-400 transition-colors group-hover:border-gray-300" />
        </div>
        <Button variant="ghostGray" size="tinyIcon" disabled>
          <MoreVerticalIcon className="h-4 w-4 text-gray-300" />
        </Button>
      </div>
      <div className="relative z-[1] flex flex-col gap-y-1">
        <div className="flex h-[150px] w-full items-center justify-center rounded border border-gray-200 bg-white transition-all duration-200 group-hover:border-gray-300">
          <span className="flex items-center gap-x-2 text-gray-400 transition-colors group-hover:text-gray-600">
            <PlusIcon className=" h-4 w-4" />
            <span className="font-medium">Add New Message</span>
          </span>
        </div>
      </div>
    </div>
  );
};

const ContentAreaSkeleton = ({ className }: { className?: string }) => {
  const widths = ["90%", "75%", "85%", "60%", "95%", "70%", "80%", "65%"];

  return (
    <div className={cn("flex flex-col gap-y-2 border-[1px] border-gray-200 p-2", className)}>
      {Array.from({ length: 8 }, (_, index) => (
        <Skeleton
          key={index}
          className="duration-720 h-4 w-full rounded-md"
          style={{ width: widths[index] }}
        />
      ))}
    </div>
  );
};

interface MediaVariableBlockProps {
  mediaVariable: string;
  blockIndex: number;
  onUpdate: (index: number, value: string) => void;
  onDelete: () => void;
  readonly?: boolean;
}

const MediaVariableBlock = ({
  mediaVariable,
  blockIndex,
  onUpdate,
  onDelete,
  readonly,
}: MediaVariableBlockProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate(blockIndex, e.target.value);
  };

  return (
    <div className="flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 focus-within:border-violet-200">
      <span className="font-mono text-sm text-violet-600">{"{"}</span>
      <input
        type="text"
        value={mediaVariable}
        onChange={handleChange}
        readOnly={readonly}
        placeholder="variableName"
        className="min-w-0 flex-1 border-none bg-transparent font-mono text-sm text-violet-600 placeholder-violet-400 outline-none focus:outline-none focus:ring-0"
      />
      <span className="font-mono text-sm text-violet-600">{"}"}</span>
      {!readonly && (
        <button
          type="button"
          onClick={onDelete}
          className="ml-1 flex h-4 w-4 items-center justify-center rounded text-violet-400 transition-colors hover:bg-violet-200 hover:text-violet-600"
          aria-label="Remove variable"
        >
          <XIcon className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

interface BlockActionsProps {
  onAddText: () => void;
  onAddFile?: (media: Media) => void;
  onAddMediaVariable?: () => void;
  hasMedia?: boolean;
  isDisabled?: boolean;
  isAlwaysVisible?: boolean;
  isSizeXS?: boolean;
  isUploading?: boolean;
}

const BlockActions = ({
  onAddText,
  onAddFile,
  onAddMediaVariable,
  hasMedia,
  isDisabled = false,
  isAlwaysVisible = false,
  isSizeXS = false,
  isUploading = false,
}: BlockActionsProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const auth = useAuth();
  const upload = useUpload();
  const isLoadingFile = upload.isLoading || isUploading;

  const handleFileChange = async (file?: File) => {
    if (!file || !auth?.userToken || !onAddFile) {
      return;
    }

    upload.mutate(
      { userToken: auth.userToken, file },
      {
        onSuccess: data => {
          if (!data) {
            return;
          }
          onAddFile({
            type: getMediaTypeFromMimeType(file.type),
            url: data.file_url,
            title: file.name,
          });
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        },
      },
    );
  };

  return (
    <div
      className={cn(
        "relative z-10 flex max-h-0 translate-y-[-2px] items-center gap-x-2 overflow-hidden opacity-0 transition-all duration-200 ease-out focus-within:max-h-8 focus-within:translate-y-0 focus-within:pt-2 focus-within:opacity-100 group-hover:max-h-8 group-hover:translate-y-0 group-hover:pt-2 group-hover:opacity-100",
        isAlwaysVisible && "max-h-8 translate-y-0 pt-2 opacity-100",
        isSizeXS && "gap-x-[2px]",
      )}
    >
      <BasicTooltip content="Add Text">
        <button
          type="button"
          onClick={onAddText}
          className="flex items-center gap-x-1 rounded-md px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          disabled={isDisabled}
        >
          <PlusIcon className="h-3 w-3" />
          {!isSizeXS && <span>Text</span>}
          {isSizeXS && <TypeOutlineIcon className="h-3 w-3" />}
        </button>
      </BasicTooltip>
      {hasMedia && onAddFile && (
        <>
          <BasicTooltip content="Add File">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isDisabled || isLoadingFile}
              className="flex items-center gap-x-1 rounded-md px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
            >
              {isLoadingFile ? (
                <div className="h-3 w-3 animate-spin rounded-full border border-gray-300 border-t-gray-600" />
              ) : (
                <PlusIcon className="h-3 w-3" />
              )}
              {!isSizeXS && <span>{isLoadingFile ? "Uploading..." : "File"}</span>}
              {isSizeXS && (
                <span>
                  <FileBoxIcon className="h-3 w-3" />
                </span>
              )}
            </button>
          </BasicTooltip>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*,audio/*,video/*,application/pdf"
            disabled={isLoadingFile}
            onChange={e => handleFileChange(e.target.files?.[0])}
          />
        </>
      )}
      {hasMedia && onAddMediaVariable && (
        <BasicTooltip content="Add Media Variable">
          <button
            type="button"
            onClick={onAddMediaVariable}
            disabled={isDisabled}
            className="flex items-center gap-x-1 rounded-md px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <PlusIcon className="h-3 w-3" />
            {!isSizeXS && <span>Media Variable</span>}
            {isSizeXS && <BracesIcon className="h-3 w-3" />}
          </button>
        </BasicTooltip>
      )}
    </div>
  );
};

const getInputVariableKeysFromText = (text: string, templateFormat: string): string[] => {
  const variableRegex = templateFormat === "jinja2" ? /\{\{\s*([^{}]+?)\s*\}\}/g : /\{([^{}]+)\}/g;
  const keys = new Set<string>();
  let match;

  while ((match = variableRegex.exec(text)) !== null) {
    const key = match[1]?.trim();
    if (key) {
      keys.add(key);
    }
  }

  return Array.from(keys);
};

function getCacheLabel(cc: CacheControl | undefined): string | undefined {
  if (!cc) {
    return undefined;
  }
  return cc.ttl === "1h" ? "1h" : "5m";
}

const CACHE_CONTROL_OPTIONS: { label: string; value: CacheControl | undefined }[] = [
  { label: "No caching", value: undefined },
  { label: "Cache (5min)", value: { type: "ephemeral" } },
  { label: "Cache (1hr)", value: { type: "ephemeral", ttl: "1h" } },
];

function CacheControlDropdown({
  cacheControl,
  cacheLabel,
  onSelect,
}: {
  cacheControl: CacheControl | undefined;
  cacheLabel: string | undefined;
  onSelect: (cc: CacheControl | undefined) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Prompt cache breakpoint"
          className={cn(
            "flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[10px] font-medium transition-colors",
            cacheLabel
              ? "border-blue-300 bg-blue-50 text-blue-600 hover:bg-blue-100"
              : "border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700",
          )}
          aria-label="Cache control"
        >
          <DatabaseIcon className="h-3 w-3" />
          {cacheLabel ?? "Cache"}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px]">
        {CACHE_CONTROL_OPTIONS.map(opt => (
          <DropdownMenuItem
            key={opt.label}
            onSelect={() => onSelect(opt.value)}
            className={cn(
              "text-xs",
              JSON.stringify(cacheControl) === JSON.stringify(opt.value) && "font-semibold",
            )}
          >
            {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// PlaygroundContentBlocks — bridges the Playground's MobX store model with
// the shared ContentBlocks renderer via blockOverrides.
//
// Block types that need Playground-specific behaviour (editing, interactive
// approval, custom accordions) are overridden here. All other block types
// fall through to the default renderer from @promptlayer/ui, so new block
// types added to the shared library propagate automatically.
// ---------------------------------------------------------------------------

interface PlaygroundContentBlocksProps {
  enableJsonOutputDetection?: boolean;
  message: IMessageStore;
  playground: ReturnType<typeof usePlayground>;
  readonly: boolean;
  isOutputMessageComplete?: boolean;
  isCompletion?: boolean;
  isLiveToolCall: boolean;
  isStreamingOutput: boolean;
  isLastMessage: boolean;
  contentAreaStyle: string;
  contentBlocksClassName?: string;
  handleFileReplace: (file: File, blockIndex: number) => void;
  /** Index of this message within playground.messages (needed for MCP approval) */
  index?: number;
}

const PlaygroundContentBlocks = observer(
  ({
    enableJsonOutputDetection = false,
    message,
    playground,
    readonly,
    isOutputMessageComplete = false,
    isCompletion,
    isLiveToolCall,
    isStreamingOutput,
    isLastMessage,
    contentAreaStyle,
    contentBlocksClassName,
    handleFileReplace,
    index,
  }: PlaygroundContentBlocksProps) => {
    // The Playground wraps each content item as { type, content, index }.
    // We need the original store indices to call mutation methods.
    const blocks = message.contentBlocks;
    const [activeScopedInputVariable, setActiveScopedInputVariable] = React.useState<{
      key: string;
      anchorRect: DOMRect;
    } | null>(null);

    // Convert to flat ContentBlock[] for the shared renderer.
    const adapted = useMemo(() => adaptPlaygroundBlocks(blocks), [blocks]);

    // Helper: resolve the MobX store index for a given array position.
    const storeIndex = useCallback(
      (arrayIdx: number) => blocks[arrayIdx]?.index ?? arrayIdx,
      [blocks],
    );

    // Helper: compute placeholder text for a text block at a given position.
    const getPlaceholder = useCallback(
      (arrayIdx: number): string | undefined => {
        if (arrayIdx === 0) {
          return message.placeholder || undefined;
        }
        if (message.role === "assistant") {
          return "";
        }
        return 'Type "/" for commands';
      },
      [message.placeholder, message.role],
    );

    const inputVariableDecorations = useInputVariableDecorations(playground, readonly);

    const handleInputVariableClick = useCallback((variableKey: string, target: HTMLElement) => {
      const nextAnchorRect = target.getBoundingClientRect();

      setActiveScopedInputVariable(current => {
        if (current?.key === variableKey) {
          return null;
        }

        return {
          key: variableKey,
          anchorRect: nextAnchorRect,
        };
      });
    }, []);

    const isAnthropicModel = useMemo(
      () =>
        playground.provider === "anthropic" ||
        (playground.provider === "vertexai" &&
          (playground.model?.name?.toLowerCase().includes("claude") ||
            playground.model?.base_model?.toLowerCase().includes("claude"))) ||
        (playground.provider === "amazon.bedrock" &&
          (playground.model?.name?.toLowerCase().includes("anthropic") ||
            playground.model?.base_model?.toLowerCase().includes("anthropic"))),
      [playground.provider, playground.model?.name, playground.model?.base_model],
    );

    const blockOverrides = useMemo(
      (): BlockOverrides => ({
        // ---- text: editable ContentArea + delete button + cache control ----
        text: (block, ctx) => {
          const idx = storeIndex(ctx.index);
          if (readonly && !block.text && (blocks.length > 1 || message.hasToolCalls)) {
            return null;
          }
          const textBlock = block as TextContent;
          const isToolMsg = message.role === "tool";
          const currentCacheControl = isToolMsg ? message.cache_control : textBlock.cache_control;
          const cacheLabel = getCacheLabel(currentCacheControl);
          const parsedJson = getPrettifiableJsonText({
            enableJsonOutputDetection,
            isLiveToolCall,
            isOutputMessageComplete,
            messageRole: message.role,
            readonly,
            text: block.text,
          });

          const fillTextHeight = Boolean(isCompletion) && ctx.index === 0;
          return (
            <div className={cn("group/textblock relative", fillTextHeight && "h-full")}>
              {parsedJson ? (
                <PrettyJsonView
                  data={parsedJson.data}
                  rawText={parsedJson.source}
                  className={cn(fillTextHeight && "h-full", contentBlocksClassName)}
                />
              ) : (
                <ContentArea
                  readOnly={readonly}
                  showLineNumbers
                  key={`${idx}-${String(readonly)}`}
                  config={{
                    snippetOverrides: playground.snippetOverrideStore.snippetOverrides,
                    inputVariables: Array.from(
                      new Set([
                        ...playground.detectedInputVariables,
                        ...getInputVariableKeysFromText(block.text, playground.template_format),
                      ]),
                    ),
                  }}
                  markdownEnabled={playground.markdownEnabled}
                  placeholder={getPlaceholder(ctx.index)}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    message.setTextContentBlockAt(idx, e.target.value)
                  }
                  value={block.text}
                  inputVariableDecorations={inputVariableDecorations}
                  onInputVariableClick={
                    readonly || playground.isInlineEditor ? undefined : handleInputVariableClick
                  }
                  fillHeight={fillTextHeight}
                  className={cn(contentAreaStyle + " w-full", contentBlocksClassName)}
                />
              )}
              <div className="absolute right-2 top-2 z-30 flex items-center gap-1 opacity-0 transition-opacity group-hover/textblock:opacity-100">
                {!readonly && isAnthropicModel && (
                  <CacheControlDropdown
                    cacheControl={currentCacheControl}
                    cacheLabel={cacheLabel}
                    onSelect={(cc: CacheControl | undefined) =>
                      isToolMsg ? message.setCacheControl(cc) : message.setCacheControlAt(idx, cc)
                    }
                  />
                )}
                {readonly && isAnthropicModel && cacheLabel && (
                  <BasicTooltip content="Prompt cache breakpoint">
                    <span className="flex items-center gap-0.5 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                      <DatabaseIcon className="h-3 w-3" />
                      {cacheLabel}
                    </span>
                  </BasicTooltip>
                )}
                {!readonly && blocks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => message.deleteContentBlockAt(idx)}
                    className="flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Delete content block"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          );
        },

        // ---- thinking: editable ThinkingAccordion ----
        thinking: (block, ctx) => {
          const idx = storeIndex(ctx.index);
          return (
            <ThinkingAccordion
              value={block.thinking}
              defaultOpen={isLiveToolCall || isStreamingOutput || isLastMessage}
              title={(isLiveToolCall && message.toolCallStatus) || "Thinking"}
              id={(block as { id?: string }).id || `thinking-${idx}`}
              copyValue={block.thinking}
            >
              <ContentArea
                markdownEnabled={playground.markdownEnabled}
                key={`${idx}-${String(readonly)}`}
                config={{
                  snippetOverrides: playground.snippetOverrideStore.snippetOverrides,
                }}
                readOnly={readonly}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  message.setThinkingContentBlockAt(idx, e.target.value)
                }
                value={block.thinking}
                className="z-[1] !min-h-[0px] w-full border-none bg-transparent p-0 text-sm shadow-none"
              />
            </ThinkingAccordion>
          );
        },

        // ---- media: deletable / replaceable ----
        media: (block, ctx) => {
          if (!message.hasMedia || isCompletion || playground.isAIPromptAssistantLoading) {
            return null;
          }
          const idx = storeIndex(ctx.index);
          const rawMedia = block.media;
          const normalizedMedia: Media = {
            ...rawMedia,
            type: detectMediaTypeFromUrl(rawMedia.url, rawMedia.type),
          };
          return (
            <MediaBlock
              media={normalizedMedia}
              onDelete={() => message.deleteContentBlockAt(idx)}
              onReplace={(file: File) => handleFileReplace(file, idx)}
              readonly={readonly}
            />
          );
        },

        // ---- media_variable: editable variable name ----
        media_variable: (block, ctx) => {
          if (!message.hasMedia || isCompletion || playground.isAIPromptAssistantLoading) {
            return null;
          }
          const idx = storeIndex(ctx.index);
          return (
            <MediaVariableBlock
              mediaVariable={block.name}
              blockIndex={idx}
              onUpdate={(i, value) => message.updateMediaVariableBlockAt(i, value)}
              onDelete={() => message.deleteContentBlockAt(idx)}
              readonly={readonly}
            />
          );
        },

        // ---- output_media: group consecutive blocks ----
        output_media: (block, ctx) => {
          // Skip if already rendered as part of a previous group
          if (ctx.index > 0 && ctx.allBlocks[ctx.index - 1]?.type === "output_media") {
            return null;
          }

          // Collect consecutive output_media blocks into a group
          const mediaGroup: {
            id?: string;
            url?: string;
            media_type?: string;
            revised_prompt?: string;
            provider_metadata?: OutputMediaProviderMetadata;
          }[] = [];
          for (let i = ctx.index; i < ctx.allBlocks.length; i++) {
            if (ctx.allBlocks[i].type !== "output_media") {
              break;
            }
            mediaGroup.push(
              ctx.allBlocks[i] as {
                id?: string;
                url?: string;
                media_type?: string;
                revised_prompt?: string;
                provider_metadata?: OutputMediaProviderMetadata;
              },
            );
          }

          const first = mediaGroup[0];
          const imageUrl = first?.url;
          const mediaType = (first?.media_type ?? "image") as "image" | "video" | "audio";
          if (
            !imageUrl ||
            (mediaType !== "image" && mediaType !== "video" && mediaType !== "audio")
          ) {
            return null;
          }

          const toContent = (mc: (typeof mediaGroup)[number]) => ({
            type: "output_media" as const,
            id: mc.id,
            url: mc.url!,
            media_type: (mc.media_type ?? "image") as "image" | "video" | "audio",
            provider_metadata: {
              ...mc.provider_metadata,
              revised_prompt: mc.provider_metadata?.revised_prompt ?? mc.revised_prompt,
            },
          });

          return (
            <OutputMediaBlock
              content={toContent(first)}
              extraImages={mediaGroup
                .slice(1)
                .filter(mc => mc.url && (mc.media_type ?? "image") === "image")
                .map(toContent)}
              readonly
              isLoading={mediaType === "audio" && isStreamingOutput && !isOutputMessageComplete}
            />
          );
        },

        // ---- code: frontend CodeAccordion ----
        code: (block, ctx) => {
          const idx = storeIndex(ctx.index);
          return (
            <CodeAccordion
              code={block.code}
              id={(block as { id?: string }).id || `code-${idx}`}
              defaultOpen={isLiveToolCall || isStreamingOutput || isLastMessage}
              containerId={(block as { container_id?: string }).container_id}
            />
          );
        },

        // ---- shell_call: frontend CodeAccordion ----
        shell_call: (block, ctx) => {
          const idx = storeIndex(ctx.index);
          const action = block.action as { commands?: string[] };
          const commands = action?.commands || [];
          return (
            <CodeAccordion
              code={commands.join("\n")}
              id={block.id || `shell-${idx}`}
              defaultOpen={isLiveToolCall || isStreamingOutput || isLastMessage}
              label="Shell"
            />
          );
        },

        // ---- apply_patch_call: frontend CodeAccordion ----
        apply_patch_call: (block, ctx) => {
          const idx = storeIndex(ctx.index);
          const op = (block.operation as { type?: string; path?: string; diff?: string }) || {};
          const label = op.path ? `${op.type || "patch"}: ${op.path}` : "Apply Patch";
          return (
            <CodeAccordion
              code={op.diff || ""}
              id={block.id || `patch-${idx}`}
              defaultOpen={isLiveToolCall || isStreamingOutput || isLastMessage}
              label={label}
            />
          );
        },

        // ---- mcp_list_tools: McpAccordion with detailed param display ----
        mcp_list_tools: (block, ctx) => {
          const idx = storeIndex(ctx.index);
          const toolCount = block.tools.length;
          const toolWord = toolCount === 1 ? "tool" : "tools";
          return (
            <McpAccordion
              id={`mcp-list-${idx}`}
              title={`${toolCount} ${toolWord} available`}
              serverLabel={block.server_label}
              defaultOpen={isLiveToolCall || isStreamingOutput}
              isLive={isLiveToolCall || isStreamingOutput}
              variant={block.error ? "error" : "default"}
            >
              <div className="px-4 py-3">
                {block.error ? (
                  <pre className="whitespace-pre-wrap break-words text-sm text-red-600">
                    {formatMcpError(block.error)}
                  </pre>
                ) : (
                  <div className="flex flex-col divide-y divide-gray-100">
                    {block.tools.map(tool => {
                      const schema = tool.input_schema as
                        | {
                            properties?: Record<string, { type?: string; description?: string }>;
                            required?: string[];
                          }
                        | undefined;
                      const params = schema?.properties ? Object.entries(schema.properties) : [];
                      const requiredParams = schema?.required ?? [];
                      return (
                        <div key={`tool-${tool.name}`} className="py-3 first:pt-0 last:pb-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-semibold text-gray-900">
                              {tool.name}
                            </span>
                            {tool.annotations?.read_only === true && (
                              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                                read-only
                              </span>
                            )}
                          </div>
                          {tool.description && (
                            <p className="mt-1 text-xs leading-relaxed text-gray-500">
                              {tool.description}
                            </p>
                          )}
                          {params.length > 0 && (
                            <div className="mt-2 rounded-md border border-gray-100 bg-white/60">
                              <div className="px-2.5 py-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">
                                Parameters
                              </div>
                              <div className="divide-y divide-gray-100">
                                {params.map(([paramName, paramDef]) => (
                                  <div
                                    key={paramName}
                                    className="flex items-center gap-2 px-2.5 py-1.5"
                                  >
                                    <code className="shrink-0 text-xs font-medium text-gray-700">
                                      {paramName}
                                    </code>
                                    {paramDef.type && (
                                      <span className="shrink-0 rounded bg-blue-50 px-1 py-0.5 text-[10px] font-medium text-blue-600">
                                        {paramDef.type}
                                      </span>
                                    )}
                                    {requiredParams.includes(paramName) && (
                                      <span className="shrink-0 text-[10px] font-medium text-red-400">
                                        required
                                      </span>
                                    )}
                                    {paramDef.description && (
                                      <span className="text-xs text-gray-400">
                                        {paramDef.description}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </McpAccordion>
          );
        },

        // ---- mcp_call: McpAccordion with markdown response ----
        mcp_call: (block, ctx) => {
          const idx = storeIndex(ctx.index);
          const hasError = Boolean(block.error);
          const title = block.name;
          const errorStr = formatMcpError(block.error);
          const copyValue = errorStr || block.output || "";

          let parsedArgs: Record<string, unknown> | null = null;
          if (block.arguments) {
            try {
              parsedArgs = JSON.parse(block.arguments);
            } catch {
              /* keep as raw string */
            }
          }

          return (
            <McpAccordion
              id={`mcp-call-${idx}`}
              title={title}
              serverLabel={block.server_label}
              defaultOpen={isLiveToolCall || isStreamingOutput || isLastMessage}
              isLive={isLiveToolCall || isStreamingOutput}
              variant={hasError ? "error" : "default"}
              copyValue={copyValue}
            >
              <div className="flex flex-col gap-3 px-4 py-3">
                {block.arguments && (
                  <div>
                    <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">
                      Arguments
                    </div>
                    {parsedArgs ? (
                      <div className="divide-y divide-gray-100 rounded-md border border-gray-100 bg-white/60">
                        {Object.entries(parsedArgs).map(([key, val]) => (
                          <div key={key} className="flex flex-col gap-0.5 px-2.5 py-2">
                            <code className="text-xs font-semibold text-gray-600">{key}</code>
                            <span className="break-words rounded bg-gray-50 px-1.5 py-1 font-mono text-xs text-gray-500">
                              {typeof val === "string" ? `"${val}"` : JSON.stringify(val, null, 2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <pre className="whitespace-pre-wrap break-words rounded-md border border-gray-100 bg-white/60 p-2.5 font-mono text-xs text-gray-600">
                        {block.arguments}
                      </pre>
                    )}
                  </div>
                )}
                {hasError && (
                  <div>
                    <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-red-400">
                      Error
                    </div>
                    <pre className="whitespace-pre-wrap break-words rounded-md border border-red-100 bg-red-50/50 p-2.5 font-mono text-xs text-red-600">
                      {errorStr}
                    </pre>
                  </div>
                )}
                {!hasError && block.output && (
                  <div>
                    <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">
                      Response
                    </div>
                    <div className="max-h-48 overflow-y-auto rounded-md border border-gray-100 bg-white/60 p-2.5">
                      <ContentArea
                        markdownEnabled={playground.markdownEnabled}
                        readOnly
                        value={block.output}
                        className="z-[1] !min-h-[0px] w-full border-none bg-transparent p-0 text-sm shadow-none"
                      />
                    </div>
                  </div>
                )}
              </div>
            </McpAccordion>
          );
        },

        // ---- mcp_approval_request: interactive approve/deny ----
        mcp_approval_request: (block, ctx) => {
          const idx = storeIndex(ctx.index);
          const title = `Approval: ${block.name}`;

          let parsedArgs: Record<string, unknown> | null = null;
          if (block.arguments) {
            try {
              parsedArgs = JSON.parse(block.arguments);
            } catch {
              /* keep as raw string */
            }
          }

          const handleApproval = (approve: boolean) => {
            if (index === undefined) {
              return;
            }
            const nextMsg = playground.messages[index + 1];
            if (nextMsg) {
              nextMsg.setApprovalResponseContent(block.id || "", approve);
            } else {
              playground.addMessage({
                id: crypto.randomUUID(),
                role: "user",
                content: [
                  {
                    type: "mcp_approval_response",
                    approval_request_id: block.id || "",
                    approve,
                  } as Content,
                ],
              });
            }
          };

          // Check if the next message already has an approval response
          let selectedApproval: boolean | null = null;
          if (index !== undefined) {
            const nextMsg = playground.messages[index + 1];
            if (nextMsg) {
              const raw = nextMsg.rawContent;
              const approvalBlock = Array.isArray(raw)
                ? raw.find(c => c.type === "mcp_approval_response")
                : undefined;
              if (approvalBlock && "approve" in approvalBlock) {
                selectedApproval = (approvalBlock as { approve: boolean }).approve;
              }
            }
          }

          return (
            <McpAccordion
              id={`mcp-approval-${idx}`}
              title={title}
              serverLabel={block.server_label}
              defaultOpen
              variant="default"
            >
              <div className="flex flex-col gap-3 px-4 py-3">
                {block.arguments &&
                  block.arguments !== "{}" &&
                  (!parsedArgs || Object.keys(parsedArgs).length > 0) && (
                    <div>
                      <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">
                        Arguments
                      </div>
                      {parsedArgs ? (
                        <div className="divide-y divide-gray-100 rounded-md border border-gray-100 bg-white/60">
                          {Object.entries(parsedArgs).map(([key, val]) => (
                            <div key={key} className="flex flex-col gap-0.5 px-2.5 py-2">
                              <code className="text-xs font-semibold text-gray-600">{key}</code>
                              <span className="break-words rounded bg-gray-50 px-1.5 py-1 font-mono text-xs text-gray-500">
                                {typeof val === "string"
                                  ? `"${val}"`
                                  : JSON.stringify(val, null, 2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <pre className="whitespace-pre-wrap break-words rounded-md border border-gray-100 bg-white/60 p-2.5 font-mono text-xs text-gray-600">
                          {block.arguments}
                        </pre>
                      )}
                    </div>
                  )}
                {readonly ? (
                  <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50/50 px-3 py-2">
                    <div className="h-2 w-2 animate-pulse rounded-full bg-amber-400" />
                    <span className="text-xs font-medium text-amber-700">Awaiting Approval</span>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={cn(
                        "flex-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
                        selectedApproval === false
                          ? "border-red-300 bg-red-100 text-red-800"
                          : "border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
                      )}
                      onClick={() => handleApproval(false)}
                    >
                      Deny
                    </button>
                    <button
                      type="button"
                      className={cn(
                        "flex-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
                        selectedApproval === true
                          ? "border-green-300 bg-green-100 text-green-800"
                          : "border-green-200 bg-green-50 text-green-700 hover:bg-green-100",
                      )}
                      onClick={() => handleApproval(true)}
                    >
                      Approve
                    </button>
                  </div>
                )}
              </div>
            </McpAccordion>
          );
        },

        // ---- mcp_approval_response, code_execution_result, server_tool_use,
        //      bash_code_execution_tool_result, text_editor_code_execution_tool_result,
        //      web_search_tool_result, shell_call_output, apply_patch_call_output,
        //      image_url, tool_use:
        //      All fall through to the default shared renderer from @promptlayer/ui ----
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        blocks,
        readonly,
        isCompletion,
        isLiveToolCall,
        isOutputMessageComplete,
        isStreamingOutput,
        isLastMessage,
        enableJsonOutputDetection,
        contentAreaStyle,
        contentBlocksClassName,
        playground.markdownEnabled,
        playground.snippetOverrideStore.snippetOverrides,
        playground.isAIPromptAssistantLoading,
        playground.isInlineEditor,
        isAnthropicModel,
        message,
        index,
        handleFileReplace,
        storeIndex,
        getPlaceholder,
        inputVariableDecorations,
        handleInputVariableClick,
      ],
    );

    return (
      <>
        <ContentBlocks
          content={adapted}
          isStreaming={isStreamingOutput}
          blockOverrides={blockOverrides}
          className={cn("gap-y-2", isCompletion && "h-full")}
        />
        {!readonly && !playground.isInlineEditor && activeScopedInputVariable && (
          <ScopedInputVariablePopover
            key={activeScopedInputVariable.key}
            variableKey={activeScopedInputVariable.key}
            anchorRect={activeScopedInputVariable.anchorRect}
            onClose={() => setActiveScopedInputVariable(null)}
          />
        )}
      </>
    );
  },
);

// ---------------------------------------------------------------------------
// Message
// ---------------------------------------------------------------------------

const Message = (props: MessageProps) => {
  const {
    message,
    enableJsonOutputDetection = false,
    isCompletion,
    isFirst,
    isOutputMessageComplete = false,
    readonly: globalReadonly,
    extraHeader,
    index,
    dragHandleProps,
    mode = "batch",
    isDragging = false,
    onSend,
    className,
    contentBlocksClassName,
    isSending,
    isToolResponseMode,
    secondaryAction,
    isBlockActionsPositionFixed,
    isBlockActionsAlwaysVisible,
    isBlockActionsSizeXS,
  } = props;
  const playground = usePlayground();
  const isChatMode = mode === PLAYGROUND_SESSION_MODES.CHAT;
  const { isFullscreen, setIsFullscreen, open } = useFullscreen();
  const message_details = MESSAGE_DETAILS[message.role];
  const [isDragOver, setIsDragOver] = React.useState(false);

  const auth = useAuth();
  const upload = useUpload();

  const readonly =
    globalReadonly ||
    playground.isXRayMode ||
    (playground.mode === "completion" && playground.operationStore.isRunning);

  const contentAreaStyle = `z-[1] rounded bg-white text-sm ${
    isFirst && !isCompletion ? "!min-h-[200px]" : ""
  }  ${message.usesMonospaceFont ? "font-mono" : ""} `;

  const leftBraces = playground.template_format === "jinja2" ? "{{" : "{";
  const rightBraces = playground.template_format === "jinja2" ? "}}" : "}";

  const isLiveToolCall =
    message.role === "assistant" && (message.isToolCallInProgress || message.isEmptyMessage);

  const isLastMessage = index !== undefined && index === playground.messages.length - 1;
  const shouldShowHoverDragHandle =
    !readonly && !playground.isAIPromptAssistantLoading && !isChatMode;
  const shouldForceShowDragHandle = shouldShowHoverDragHandle && isDragging;
  const isStreamingOutput =
    message.role === "assistant" &&
    playground.operationStore.isRunning &&
    (isLastMessage || playground.operationStore.isMessageInActiveOperation(message.id));

  const isAnthropicModelMsg = useMemo(
    () =>
      playground.provider === "anthropic" ||
      (playground.provider === "vertexai" &&
        (playground.model?.name?.toLowerCase().includes("claude") ||
          playground.model?.base_model?.toLowerCase().includes("claude"))) ||
      (playground.provider === "amazon.bedrock" &&
        (playground.model?.name?.toLowerCase().includes("anthropic") ||
          playground.model?.base_model?.toLowerCase().includes("anthropic"))),
    [playground.provider, playground.model?.name, playground.model?.base_model],
  );

  const handleFileDrop = useCallback(
    (file: File) => {
      if (!auth?.userToken || readonly || !message.hasMedia) {
        return;
      }

      upload.mutate(
        { userToken: auth.userToken, file },
        {
          onSuccess: data => {
            if (!data) {
              return;
            }
            message.addFileBlockWithMedia({
              type: getMediaTypeFromMimeType(file.type),
              url: data.file_url,
              title: file.name,
            });
          },
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth?.userToken, readonly, message.hasMedia, upload],
  );

  const handleFileReplace = (file: File, blockIndex: number) => {
    if (!auth?.userToken || readonly) {
      return;
    }

    upload.mutate(
      { userToken: auth.userToken, file },
      {
        onSuccess: data => {
          if (!data) {
            return;
          }
          message.updateMediaBlockAt(blockIndex, {
            type: getMediaTypeFromMimeType(file.type),
            url: data.file_url,
            title: file.name,
          });
        },
      },
    );
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (readonly || !message.hasMedia) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (readonly || !message.hasMedia) {
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    files.forEach(file => {
      const isValidType =
        file.type.startsWith("image/") ||
        file.type.startsWith("video/") ||
        file.type.startsWith("audio/") ||
        file.type === "application/pdf";
      if (isValidType) {
        handleFileDrop(file);
      }
    });
  };

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      if (readonly || !message.hasMedia) {
        return;
      }

      // Get files from clipboard (check both files and items for browser compatibility)
      let files = Array.from(e.clipboardData.files);
      if (files.length === 0 && e.clipboardData.items) {
        const items = Array.from(e.clipboardData.items);
        files = items
          .filter(item => item.kind === "file")
          .map(item => item.getAsFile())
          .filter((file): file is File => file !== null);
      }

      // No files - let text paste pass through to ContentArea
      if (files.length === 0) {
        return;
      }

      // We have files - prevent default and handle them
      e.preventDefault();
      e.stopPropagation();

      // Filter for valid media types
      files.forEach(file => {
        const isValidType =
          file.type.startsWith("image/") ||
          file.type.startsWith("video/") ||
          file.type.startsWith("audio/") ||
          file.type === "application/pdf";
        if (isValidType) {
          handleFileDrop(file);
        }
      });
    },
    [readonly, message.hasMedia, handleFileDrop],
  );

  return (
    <div
      className={cn(
        "group relative flex w-full flex-col gap-y-1 rounded-lg px-3 py-3 transition-all duration-150 hover:bg-gray-50",
        isChatMode ? "min-h-0" : "h-full",
        isDragOver && message.hasMedia && "bg-blue-50 ring-2 ring-inset ring-blue-400",

        className,
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onPaste={handlePaste}
    >
      {!isCompletion && (
        <div
          className={cn("flex items-center justify-between gap-x-2 pb-2", isChatMode && "hidden")}
        >
          <div className={cn(isChatMode ? "h-auto" : "h-full", "flex w-full flex-col")}>
            {extraHeader}

            {!isChatMode && (
              <div className="flex w-full items-center gap-x-2">
                <div className="flex w-full items-center gap-x-2">
                  <div
                    className={cn(
                      "flex min-w-0 items-center overflow-hidden transition-all duration-200 ease-out",
                      shouldShowHoverDragHandle && "group-hover:gap-x-2",
                      shouldForceShowDragHandle && "gap-x-2",
                    )}
                  >
                    {shouldShowHoverDragHandle && (
                      <div
                        className={cn(
                          "w-0 -translate-x-1 opacity-0 transition-all duration-200 ease-out focus-within:w-5 focus-within:translate-x-0 focus-within:opacity-100 group-hover:w-5 group-hover:translate-x-0 group-hover:opacity-100",
                          shouldForceShowDragHandle && "w-5 translate-x-0 opacity-100",
                        )}
                      >
                        <BasicTooltip content="Drag to reorder messages">
                          <div
                            {...(dragHandleProps || {})}
                            className={`flex h-7 w-5 items-center justify-center rounded-md transition-all duration-150 ease-out hover:bg-gray-100 focus:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                              readonly
                                ? "cursor-not-allowed opacity-30"
                                : "cursor-grab active:cursor-grabbing active:bg-gray-200"
                            }`}
                            role="button"
                            tabIndex={readonly ? -1 : 0}
                            aria-label={`Drag handle for ${message.role} message ${
                              index ? `${index + 1}` : ""
                            }`}
                            onKeyDown={e => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                // Focus management for keyboard users
                                e.currentTarget.focus();
                              }
                            }}
                          >
                            <GripVertical
                              height={14}
                              width={14}
                              className={
                                readonly
                                  ? "text-gray-300"
                                  : "text-gray-500 transition-colors hover:text-gray-700"
                              }
                              aria-hidden="true"
                            />
                          </div>
                        </BasicTooltip>
                      </div>
                    )}

                    <div
                      className={cn(
                        "flex shrink-0 items-center transition-all duration-200 ease-out",
                        shouldShowHoverDragHandle && "group-hover:translate-x-1",
                        shouldForceShowDragHandle && "translate-x-1",
                      )}
                    >
                      <RoleSelector
                        key={message.role}
                        selected={message.role}
                        readonly={readonly || playground.isAIPromptAssistantLoading}
                        onSelect={message.setRole}
                        mode={mode}
                      />
                    </div>
                  </div>

                  {message.hasName &&
                    (!readonly || (readonly && message.name)) &&
                    message.outputMediaContent.length === 0 && (
                      <Input
                        value={message.name}
                        disabled={readonly}
                        onChange={e => message.setName(e.target.value)}
                        className="w-[200px] shrink-0 font-mono"
                        placeholder={message.role === "tool" ? "Tool ID" : "Function Name"}
                      />
                    )}

                  <div className="relative z-20 ml-auto flex items-center gap-x-2">
                    <MessageActions
                      readonly={readonly}
                      onCopy={() => navigator.clipboard.writeText(message.fullTextContent)}
                      onMaximize={open}
                      onDelete={() => runInAction(() => playground.deleteMessage(message.id))}
                      disabled={playground.isAIPromptAssistantLoading}
                      showOnGroupHover
                      showResponsivePriorityActions
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {message.parsingError && (
        <div className="mb-1">
          <MessageParsingError message={message} />
        </div>
      )}
      <div
        className={cn("relative flex w-full flex-col gap-y-1", isCompletion ? "h-full" : "h-auto")}
      >
        {playground.isPendingNewMessage && isCompletion && !playground.isImageGenerationMode && (
          <div className="absolute right-2 top-2 z-20">
            <LoadingSpinner size={5} />
          </div>
        )}
        <div className={cn("relative w-full", isCompletion ? "h-full" : "h-auto")}>
          {playground.isAIPromptAssistantLoading && !readonly ? (
            <ContentAreaSkeleton className={contentAreaStyle} />
          ) : (
            <CollapsibleMessageContent
              disabled={isCompletion || isLiveToolCall || isStreamingOutput}
              className={cn("w-full", isCompletion ? "h-full" : "h-auto")}
              fillHeight={Boolean(isCompletion)}
            >
              <div
                className={cn("flex w-full flex-col gap-y-2", isCompletion ? "h-full" : "h-auto")}
              >
                <MessageContentDisplay
                  isLiveToolCall={isLiveToolCall}
                  isToolCallInProgress={message.isToolCallInProgress}
                  hasThinkingContent={Boolean(message.thinkingContent)}
                  hasCodeContent={Boolean(message.codeContent)}
                  hasOutputMediaContent={message.outputMediaContent.length > 0}
                  readonly={readonly}
                  toolCallStatus={message.toolCallStatus || "Generating..."}
                  messageRole={message.role}
                  message={message}
                  leftBraces={leftBraces}
                  rightBraces={rightBraces}
                >
                  <PlaygroundContentBlocks
                    enableJsonOutputDetection={enableJsonOutputDetection}
                    message={message}
                    playground={playground}
                    readonly={readonly}
                    isOutputMessageComplete={isOutputMessageComplete}
                    isCompletion={isCompletion}
                    isLiveToolCall={isLiveToolCall}
                    isStreamingOutput={isStreamingOutput}
                    isLastMessage={isLastMessage}
                    contentAreaStyle={contentAreaStyle}
                    contentBlocksClassName={contentBlocksClassName}
                    handleFileReplace={handleFileReplace}
                    index={index}
                  />
                </MessageContentDisplay>
              </div>
            </CollapsibleMessageContent>
          )}

          {isCompletion && !playground.isSnippet && (
            <div className="absolute right-2 top-2 z-20 opacity-0 transition-opacity group-hover:opacity-100">
              <MessageActions
                readonly={readonly}
                onCopy={() => navigator.clipboard.writeText(message.fullTextContent)}
                onMaximize={open}
                onDelete={() => runInAction(() => playground.deleteMessage(message.id))}
                disabled={playground.isAIPromptAssistantLoading}
                showDelete={false}
                showOnGroupHover
                showResponsivePriorityActions
              />
            </div>
          )}
        </div>
        {message_details && (
          <div className="text-xs text-gray-500">
            {message_details}
            {message.role === "placeholder" && (
              <>
                {" "}
                <a
                  href="https://docs.promptlayer.com/features/prompt-registry/placeholder-messages"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  Learn more
                </a>
              </>
            )}
          </div>
        )}
      </div>
      <div
        className={cn(
          isBlockActionsPositionFixed &&
            "sticky bottom-0 z-20 rounded-lg bg-gray-200/60 px-2 pb-2 pt-0 backdrop-blur-sm",
        )}
      >
        {(isBlockActionsAlwaysVisible ||
          (!readonly && !isCompletion && !playground.isAIPromptAssistantLoading)) &&
          message.role !== "assistant" &&
          message.role !== "placeholder" &&
          !message.hasApprovalResponseContent && (
            <BlockActions
              onAddText={() => message.addTextContentBlock()}
              onAddFile={
                message.hasMedia ? media => message.addFileBlockWithMedia(media) : undefined
              }
              onAddMediaVariable={
                message.hasMedia ? () => message.addMediaVariableBlock() : undefined
              }
              hasMedia={message.hasMedia}
              isAlwaysVisible={isBlockActionsAlwaysVisible}
              isDisabled={playground.isAIPromptAssistantLoading}
              isSizeXS={isBlockActionsSizeXS}
              isUploading={upload.isLoading}
            />
          )}
        {((!readonly && message.canAddToolCalls) || message.hasToolCalls) && !isCompletion && (
          <ToolCalls
            readonly={readonly}
            toolCalls={message.tool_calls}
            onChangeToolCall={toolCall => message.updateToolCall(toolCall)}
            onDeleteToolCall={toolCall => message.deleteToolCall(toolCall._id!)}
            onAddToolCall={() =>
              message.addToolCall({
                id: Math.random().toString(36).substring(7),
                name: "",
                arguments: "",
              })
            }
            onSetToolCallCacheControl={(id, cc) => message.setToolCallCacheControl(id, cc)}
            isAnthropicModel={isAnthropicModelMsg}
          />
        )}
        <FullscreenDialog
          isOpen={isFullscreen}
          onOpenChange={setIsFullscreen}
          message={message}
          readonly={readonly}
          showLineNumbers
          enableJsonOutputDetection={enableJsonOutputDetection}
          isOutputMessageComplete={isOutputMessageComplete}
        />
        <Citations messages={[message]} />

        {onSend && (
          <div className="relative z-20">
            <MessageSubmitButton
              onSend={onSend}
              isSending={isSending}
              isToolResponseMode={isToolResponseMode}
              secondaryAction={secondaryAction}
              message={message}
              className="absolute -bottom-1 right-0 z-20"
              isSizeXS={isBlockActionsSizeXS}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default withExternalChanges(observer(Message));
